import { useNavigate } from "react-router-dom";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

interface LoginSignupModalProps {
  open: boolean;
  onClose: () => void;
  /** Path to return to after auth (defaults to current location). */
  returnTo?: string;
  title?: string;
  message?: string;
}

/**
 * Lightweight auth gate modal. Shown when an anonymous visitor tries to perform
 * an action that requires a logged-in account (e.g. generating media in the AI
 * chat). Routes to the shared LoginPage, which has both sign-in and sign-up tabs.
 */
const LoginSignupModal: React.FC<LoginSignupModalProps> = ({
  open,
  onClose,
  returnTo,
  title = "Sign in to continue",
  message = "Create a free account or sign in to generate content with the AI assistant.",
}) => {
  const navigate = useNavigate();

  useBodyScrollLock(open);

  if (!open) return null;

  const goToAuth = (mode: "login" | "signup") => {
    const from = returnTo ?? window.location.pathname;
    navigate("/login", { state: { from, mode } });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-accent text-lg font-bold text-white shadow-glow">
          G
        </div>
        <h2 className="text-center text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-2 text-center text-sm text-gray-500">{message}</p>

        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => goToAuth("signup")}
            className="btn-primary btn-lg w-full"
          >
            Sign up — it's free
          </button>
          <button
            type="button"
            onClick={() => goToAuth("login")}
            className="btn-ghost btn-lg w-full"
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={onClose}
            className="mt-1 text-center text-sm font-medium text-gray-400 hover:text-gray-600"
          >
            Keep browsing
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginSignupModal;
